import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { RatesProfile } from '@models/superaccount/rates-profile';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { LoaderService } from '@services/loader.service';
import { SuperaccountService } from '@services/superaccount.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { getCurrencySymbol } from '@utils/utils';
import { finalize } from 'rxjs/operators';
import { GridReadyEvent } from 'ag-grid-community';
import { User } from '@models/user';
import { UserService } from '@services/user.service';
import { PrefixLabels } from '@services/subaccount.service';

@Component({
  selector: 'wsb-reseller-price',
  templateUrl: './reseller-price.component.html',
  styleUrls: ['./reseller-price.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class ResellerPriceComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;
  @ViewChild('modalDelete') modalDelete;

  user: User | null;

  // dto
  deleteRatesProfileData: RatesProfile;

  // table
  tableColumnDefs = null;
  tableGridOptions = null;
  paginationPageSize = 10;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  // modal
  modalDeleteModal: NgbModalRef;

  labelPricePerMessage = ['credit_type_gp', 'credit_type_gs', 'credit_type_ti', 'credit_type_si', 'credit_type_ad', 'credit_type_ee'];
  gridAnimationState = 'hide';

  constructor(
    private modalService: NgbModal,
    private atlasToast: AtlasToastService,
    private superaccountService: SuperaccountService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loaderService.show();
    this.getUser();

    const that = this;
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'reseller_prices_table_id',
        field: 'idRateProfile'
      },
      {
        headerNameTranslate: 'common_name',
        field: 'name'
      },
      {
        headerNameTranslate: 'reseller_prices_create_prices',
        field: 'shared',
        sortable: false,
        cellRenderer: ppm => {
          let result = '';
          let findNation = false;
          if (ppm?.data?.pricePerMessage) {
            ppm.data.pricePerMessage.forEach(p => {
              const mt = p.type.toLowerCase();
              const cost = p.cost;
              const idNation = p.idDestinationNation;
              if (!(idNation && mt === 'gp' && idNation !== this.user.nation)) {
                result = result + this.getLabelPricePerMessage(mt, cost);
              } else {
                findNation = true;
              }
            });
            if (findNation) {
              result += this.getLabelsTranslate('foreign_countries_rate_plan_found');
            }
          }
          return result;
        },
        cellStyle: { 'line-height': '20px', 'overflow-y': 'auto' },
        cellClass: 'center-child'
      },
      {
        headerNameTranslate: 'reseller_prices_create_default',
        field: 'defaultAmount',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        cellRendererParams: {
          content: params => (params && params.data ? getCurrencySymbol() + ' ' + params.data.defaultAmount : '')
        }
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        resizable: false,
        cellRendererParams: {
          items: [
            {
              label: 'common_edit',
              onClick: e => this.editRatesProfile(e)
            },
            {
              label: 'common_delete',
              onClick: e => this.openModalDelete(e)
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];

    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      rowHeight: 115,
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        // message: ''
      }
    });
  }

  getUser() {
    this.userService.getAuthenticationState().subscribe(loggedUser => {
      if (loggedUser) {
        this.user = loggedUser;
      }
    });
  }

  reloadList(startRow?, endRow?, sort?) {
    return this.superaccountService
      .getListRatesProfile(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? this.getColId(sort[0].colId) : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  getColId(id: string) {
    if (id === 'idRateProfile') {
      return 'ID_RATE_PROFILE';
    }
    if (id === 'name') {
      return 'NAME';
    }
  }

  private getLabelPricePerMessage(label: string, item?: string): string {
    return item ? this.getLabelsTranslate(PrefixLabels.CREDIT_TYPE + label) + ': ' + item + ' ' + getCurrencySymbol() + '<br>' : '';
  }

  // private getSmsAvailabilityTranslate(label: string, item?: string, idNation?: string): string {
  //   const valueLabel =
  //     idNation && label === 'gp' ? this.findLabelNation(idNation) : label ? this.getLabelsTranslate(PrefixLabels.CREDIT_TYPE + label) : '';
  //   return valueLabel + ': ' + item;
  // }

  private getLabelsTranslate(label: string) {
    let labels;
    this.translateService.get(label).subscribe(l => {
      labels = l;
    });
    return labels;
  }

  // call creating page
  createRateProfile(): void {
    this.router.navigate(['/reseller/prices/edit']);
  }

  // call editing page
  editRatesProfile(data) {
    this.router.navigate(['/reseller/prices/edit', data.idRateProfile]);
  }

  openModalDelete(data?: any): void {
    this.deleteRatesProfileData = data;
    this.modalDeleteModal = this.modalService.open(this.modalDelete);
    this.modalDeleteModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteForm() {
    const that = this;
    this.superaccountService.deleteRateProfile(this.deleteRatesProfileData.idRateProfile).subscribe(
      () => {
        this.atlasToast.success('common_success', null);
      },
      () => {
        this.atlasToast.error('common_error', null);
      },
      () => {}
    );
    that.grid.refresh();
    this.modalDeleteModal.close();
  }
}
